import { Volume2, VolumeX } from 'lucide-react';
import { PrayerDisplay } from '../types/prayer';

interface PrayerCardProps {
  prayer: PrayerDisplay;
  isNext: boolean;
  timeUntilNext: string;
  isMuted: boolean;
  onToggleMute: () => void;
  is10MinAlert?: boolean;
  showBoomToggle?: boolean;
  boomEnabled?: boolean;
  onToggleBoom?: () => void;
}

export function PrayerCard({
  prayer,
  isNext,
  timeUntilNext,
  isMuted,
  onToggleMute,
  is10MinAlert = false,
  showBoomToggle = false,
  boomEnabled = false,
  onToggleBoom
}: PrayerCardProps) {
  const isAlert = isNext && is10MinAlert;

  return (
    <div
      className={`relative p-3 sm:p-5 md:p-8 lg:p-12 rounded-xl sm:rounded-2xl shadow-lg transition-all duration-300 transform flex flex-col justify-center overflow-hidden ${
        isAlert
          ? 'bg-gradient-to-br from-red-500 to-red-600 text-white ring-2 sm:ring-4 ring-red-300 scale-110 animate-pulse'
          : isNext
          ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white ring-2 sm:ring-4 ring-emerald-300 scale-105'
          : 'bg-white text-gray-800 hover:shadow-xl hover:scale-105'
      }`}
      style={{ minHeight: 'clamp(350px, 70vh, 650px)' }}
    >
      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 cursor-pointer drop-shadow-lg" onClick={onToggleMute}>
        {isMuted ? (
          <VolumeX className="w-6 h-6 sm:w-7 sm:h-7 md:w-9 md:h-9 lg:w-12 lg:h-12 opacity-70 hover:opacity-100" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
        ) : (
          <Volume2 className="w-6 h-6 sm:w-7 sm:h-7 md:w-9 md:h-9 lg:w-12 lg:h-12 opacity-70 hover:opacity-100" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
        )}
      </div>

      {showBoomToggle && onToggleBoom && (
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex items-center gap-1.5 sm:gap-2 md:gap-3 bg-white/20 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-1 md:px-4 md:py-2 rounded-full" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>
          <span className="text-xs sm:text-sm md:text-base lg:text-lg font-medium" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.3))' }}>Boom</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleBoom();
            }}
            className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 md:h-8 md:w-14 lg:h-10 lg:w-16 items-center rounded-full transition-colors ${
              boomEnabled ? 'bg-emerald-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 sm:h-5 sm:w-5 md:h-7 md:w-7 lg:h-9 lg:w-9 transform rounded-full bg-white transition-transform ${
                boomEnabled ? 'translate-x-4 sm:translate-x-5 md:translate-x-6 lg:translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}

      <div className="text-center space-y-2 sm:space-y-3 md:space-y-5 lg:space-y-8 flex-grow flex flex-col justify-center py-3 sm:py-4 md:py-6 px-2 sm:px-3 md:px-4">
        <div
          className={`font-bold leading-tight break-words ${isNext ? 'text-white' : 'text-emerald-600'}`}
          style={{
            fontSize: 'clamp(2.5rem, 10vw, 12rem)',
            filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.3))'
          }}
        >
          {prayer.nameArabic}
        </div>
        <div
          className={`font-black break-words px-1 sm:px-2 ${isNext ? 'text-emerald-50' : 'text-gray-700'}`}
          style={{
            fontSize: 'clamp(2rem, 8vw, 9rem)',
            filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.3))',
            lineHeight: '1.1'
          }}
        >
          {prayer.name}
        </div>
        <div
          className={`font-extrabold leading-tight break-words ${isNext ? 'text-white' : 'text-gray-900'}`}
          style={{
            fontSize: 'clamp(2.5rem, 9vw, 10rem)',
            filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.3))'
          }}
        >
          {prayer.time}
        </div>
      </div>

      {isNext && timeUntilNext && (
        <div className="text-center mt-auto pt-2 sm:pt-3 md:pt-4">
          <span
            className="inline-block bg-white/20 backdrop-blur-sm px-3 py-1.5 sm:px-5 sm:py-2 md:px-8 md:py-4 rounded-full font-bold break-words"
            style={{
              fontSize: 'clamp(1.25rem, 5vw, 5rem)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
            }}
          >
            {timeUntilNext}
          </span>
        </div>
      )}
    </div>
  );
}
